import * as THREE from 'three'
import { OctreeGeometry, OctreeGeometryNode } from '@/plugins/pointCloud/core/OctreeGeometry'
import type { WorkerPool } from '@/plugins/pointCloud/core/WorkerPool'
import type { PotreeConfig } from '@/plugins/pointCloud/loaders/types'
import { getLoggerManager } from '@/shared/logging'
import { PointAttribute, PointAttributes, PointAttributeTypes } from './PointAttributes'

const log = getLoggerManager().getLogger('pointcloud')
const diagnosticsLog = getLoggerManager().getLogger('pointcloud:diagnostics')
const LARGE_NODE_BUFFER_BYTES = 16 * 1024 * 1024
const NODE_DIAGNOSTIC_INTERVAL_MS = 15000
let lastNodeDiagnosticAt = 0

/** 加载计数器接口，用于跟踪节点加载数量 */
export interface LoadingCounter {
  /** 节点开始加载时调用，+1 */
  increment(): void
  /** 节点加载结束时调用，-1 */
  decrement(): void
}

export type NodeLoadStartKind = 'initial' | 'retry' | 'reload'

export interface NodeLoadStartedEvent {
  node: OctreeGeometryNode
  kind: NodeLoadStartKind
}

export type NodeLoadStartedHandler = (event: NodeLoadStartedEvent) => void

export type NodeLoadSettledStatus = 'loaded' | 'structural' | 'failed' | 'aborted' | 'late-dropped'

export interface NodeLoadSettledEvent {
  node: OctreeGeometryNode
  status: NodeLoadSettledStatus
  reason?: string
}

export type NodeLoadSettledHandler = (event: NodeLoadSettledEvent) => void

/** NodeLoader 依赖注入接口 */
export interface NodeLoaderDeps {
  /** Worker 池，复用 PotreeV2Decoder Worker */
  workerPool: WorkerPool
  /** Worker URL，支持 Promise 以兼容按需加载 */
  workerUrl: string | Promise<string>
  /** 加载计数器，用于统计并发加载数 */
  counter: LoadingCounter
  /** Potree 运行时配置，传入节点时复用 */
  potreeConfig: PotreeConfig
  /** 节点加载完成/失败回调，用于外部按需渲染唤醒 */
  onNodeLoadSettled?: NodeLoadSettledHandler
  /** 节点加载开始回调，用于累计 initial/retry/reload 运行时诊断。 */
  onNodeLoadStarted?: NodeLoadStartedHandler
}

export interface NodeLoaderOptions {
  /** 是否在节点资源 fetch 请求中携带 Cookie 等凭证 */
  withCredentials?: boolean
}

function createAbortError(): DOMException {
  return new DOMException('Aborted', 'AbortError')
}

function createFetchInit(options: NodeLoaderOptions, init: RequestInit = {}): RequestInit {
  return {
    ...init,
    ...(options.withCredentials === true ? { credentials: 'include' as RequestCredentials } : {}),
  }
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function nowMs(): number {
  return globalThis.performance?.now?.() ?? Date.now()
}

function toMB(bytes: number): string {
  return (bytes / 1024 / 1024).toFixed(1)
}

function bigIntToFiniteNumber(value: bigint | undefined): number {
  if (value === undefined) {
    return 0
  }
  const numericValue = Number(value)
  return Number.isFinite(numericValue) ? numericValue : 0
}

function assertPartialContentResponse(response: Response, resourceName: string): void {
  if (response.status !== 206) {
    throw new Error(`${resourceName} Range 请求返回异常状态: ${response.status}`)
  }
}

function assertExpectedByteLength(
  buffer: ArrayBuffer,
  expectedBytes: number,
  resourceName: string
): void {
  if (buffer.byteLength !== expectedBytes) {
    throw new Error(
      `${resourceName} Range 响应长度不匹配: expected=${expectedBytes}, actual=${buffer.byteLength}`
    )
  }
}

function shouldLogNodeDiagnostic(bytes: number): boolean {
  const now = getLoggerManager().now()
  if (
    bytes >= LARGE_NODE_BUFFER_BYTES ||
    now - lastNodeDiagnosticAt >= NODE_DIAGNOSTIC_INTERVAL_MS
  ) {
    lastNodeDiagnosticAt = now
    return true
  }
  return false
}

function getWorkerFailureMessage(event?: Event): string {
  const message = (event as { message?: unknown } | undefined)?.message
  if (typeof message === 'string' && message.length > 0) {
    return message
  }

  return event?.type ? `worker ${event.type}` : 'worker failed'
}

/**
 * 根据主资源 URL 解析同目录下的兄弟资源 URL。
 *
 * @param fileUrl - 原始资源 URL，支持 query-path、local-file、普通 HTTP 三种形式
 * @param siblingFileName - 同目录下的兄弟文件名
 * @returns 拼接后的兄弟资源 URL
 */
export function resolveSiblingResourceUrl(fileUrl: string, siblingFileName: string): string {
  const queryPathMatch = fileUrl.match(/^([^?]+\?path=)([^#]+)(.*)$/)
  if (queryPathMatch) {
    const prefix = queryPathMatch[1]
    const encodedPath = queryPathMatch[2]
    let decodedPath = encodedPath
    try {
      decodedPath = decodeURIComponent(encodedPath)
    } catch {
      decodedPath = encodedPath
    }

    const lastSlash = Math.max(decodedPath.lastIndexOf('/'), decodedPath.lastIndexOf('\\'))
    const baseDir = lastSlash >= 0 ? decodedPath.substring(0, lastSlash + 1) : decodedPath
    return prefix + encodeURIComponent(`${baseDir}${siblingFileName}`)
  }

  const localFileMatch = fileUrl.match(/^(https?:\/\/[^/]+\/local-file\/)([^?#]+)(.*)$/)
  if (localFileMatch) {
    const prefix = localFileMatch[1]
    const encodedPath = localFileMatch[2]
    let decodedPath = encodedPath
    try {
      decodedPath = decodeURIComponent(encodedPath)
    } catch {
      decodedPath = encodedPath
    }

    const lastSlash = Math.max(decodedPath.lastIndexOf('/'), decodedPath.lastIndexOf('\\'))
    const separator = decodedPath.includes('\\') ? '\\' : '/'
    const baseDir =
      lastSlash >= 0 ? decodedPath.substring(0, lastSlash + 1) : `${decodedPath}${separator}`
    return prefix + encodeURIComponent(`${baseDir}${siblingFileName}`)
  }

  return `${fileUrl}/../${siblingFileName}`
}

/** 节点数据加载器，负责按需加载单个八叉树节点 */
export class NodeLoader {
  /** 主资源 URL（metadata.json） */
  url: string
  /** metadata.json 解析结果 */
  metadata: any
  /** PointAttributes 实例 */
  attributes: any
  /** 几何坐标缩放因子 */
  scale: any
  /** 几何坐标偏移量 */
  offset: any

  private workerPool: WorkerPool
  private workerUrl: string | Promise<string>
  private counter: LoadingCounter
  private readonly onNodeLoadStarted?: NodeLoadStartedHandler
  private readonly onNodeLoadSettled?: NodeLoadSettledHandler
  /** 传递给 OctreeGeometryNode 的 PotreeConfig 兼容对象 */
  readonly potreeConfig: PotreeConfig
  private readonly options: NodeLoaderOptions
  private disposed = false
  private generation = 0
  private readonly activeControllers = new Set<AbortController>()
  private readonly activeCancelers = new Set<() => void>()
  private readonly successfullyLoadedNodes = new WeakSet<OctreeGeometryNode>()

  constructor(deps: NodeLoaderDeps, url: string, options: NodeLoaderOptions = {}) {
    this.url = url
    this.workerPool = deps.workerPool
    this.workerUrl = deps.workerUrl
    this.counter = deps.counter
    this.onNodeLoadStarted = deps.onNodeLoadStarted
    this.onNodeLoadSettled = deps.onNodeLoadSettled
    this.potreeConfig = deps.potreeConfig
    this.options = options
  }

  /** 当前加载器是否已进入不可恢复的销毁状态。 */
  get isDisposed(): boolean {
    return this.disposed
  }

  /**
   * 加载并解析指定节点，读取其二进制数据并填充几何属性。
   *
   * @param node - 待加载的八叉树几何节点
   * @param signal - 可选 AbortSignal，中止时拒绝加载
   */
  async load(node: OctreeGeometryNode, signal?: AbortSignal): Promise<void> {
    if (node.loaded || node.loading) {
      return
    }
    if (node.loadPermanentlyFailed || (node.loadFailed && nowMs() < node.retryAfter)) {
      return
    }
    if (this.disposed || node.disposed || node.octreeGeometry.disposed || signal?.aborted) {
      return
    }

    const requestToken = node.beginLoad(signal)
    if (requestToken === null) {
      return
    }
    this.counter.increment()
    const startKind: NodeLoadStartKind = this.successfullyLoadedNodes.has(node)
      ? 'reload'
      : node.loadErrorCount > 0
        ? 'retry'
        : 'initial'
    this.onNodeLoadStarted?.({ node, kind: startKind })

    const controller = new AbortController()
    const taskGeneration = this.generation
    let worker: Worker | undefined
    let workerUrl: string | undefined
    let finished = false
    let workerReturned = false
    let counterReleased = false
    let settledNotified = false
    let abortHandler: (() => void) | undefined
    let cancelActiveTask: () => void = () => {}

    if (signal) {
      abortHandler = () => controller.abort()
      if (signal.aborted) {
        controller.abort()
      } else {
        signal.addEventListener('abort', abortHandler, { once: true })
      }
    }
    this.activeControllers.add(controller)

    const cleanup = () => {
      if (signal && abortHandler) {
        signal.removeEventListener('abort', abortHandler)
      }
      this.activeControllers.delete(controller)
      this.activeCancelers.delete(cancelActiveTask)
    }
    const isCanceled = () =>
      this.disposed ||
      this.generation !== taskGeneration ||
      controller.signal.aborted ||
      !node.isLoadTokenCurrent(requestToken) ||
      node.disposed ||
      node.octreeGeometry.disposed
    const releaseCounter = () => {
      if (counterReleased) {
        return
      }
      counterReleased = true
      this.counter.decrement()
    }
    const returnWorker = () => {
      if (!worker || !workerUrl || workerReturned) {
        return
      }
      workerReturned = true
      worker.onmessage = null
      worker.onerror = null
      worker.onmessageerror = null
      this.workerPool.returnWorker(workerUrl, worker)
    }
    const discardWorker = () => {
      if (!worker || !workerUrl || workerReturned) {
        return
      }
      workerReturned = true
      worker.onmessage = null
      worker.onerror = null
      worker.onmessageerror = null
      this.workerPool.discardWorker(workerUrl, worker)
    }
    const resetNode = () => {
      node.cancelLoad(requestToken)
    }
    const settle = (status: NodeLoadSettledStatus, reason?: string) => {
      if (settledNotified) {
        return
      }
      settledNotified = true
      if (status === 'loaded') {
        this.successfullyLoadedNodes.add(node)
      }
      this.notifyNodeLoadSettled(node, status, reason)
    }
    const markNodeLoadFailed = (reason?: string) => {
      node.markLoadFailed(reason, requestToken)
    }
    const finishWithoutGeometry = () => {
      if (finished) {
        return
      }
      finished = true
      returnWorker()
      resetNode()
      releaseCounter()
      cleanup()
    }
    cancelActiveTask = () => {
      controller.abort()
      if (finished) {
        return
      }
      if (!worker) {
        return
      }
      finished = true
      discardWorker()
      resetNode()
      releaseCounter()
      cleanup()
      settle('aborted')
    }
    this.activeCancelers.add(cancelActiveTask)

    try {
      if (node?.nodeType === 2) {
        await this.loadHierarchy(node, controller.signal)
        if (isCanceled()) {
          throw createAbortError()
        }
      }

      const { byteOffset, byteSize } = node

      if (byteSize === 0n) {
        this.normalizeZeroByteNode(node)
        node.completeStructural(requestToken)
        finished = true
        releaseCounter()
        cleanup()
        settle('structural')
        return
      }

      const urlOctree = resolveSiblingResourceUrl(this.url, 'octree.bin')

      const first = byteOffset
      const last = byteOffset! + byteSize! - 1n

      const response = await fetch(urlOctree, {
        headers: {
          Range: `bytes=${first}-${last}`,
        },
        signal: controller.signal,
        ...(this.options.withCredentials === true
          ? { credentials: 'include' as RequestCredentials }
          : {}),
      })

      assertPartialContentResponse(response, 'octree.bin')
      const buffer = await response.arrayBuffer()
      const inputBytes = buffer.byteLength
      const expectedBytes = bigIntToFiniteNumber(byteSize)
      assertExpectedByteLength(buffer, expectedBytes, 'octree.bin')
      if (shouldLogNodeDiagnostic(Math.max(inputBytes, expectedBytes))) {
        diagnosticsLog.info(
          `[OctreeLoader] node input loaded, name=${node.name}, level=${node.level}, numPoints=${node.numPoints}, expectedMB=${toMB(expectedBytes)}, bufferMB=${toMB(inputBytes)}`
        )
      }
      if (isCanceled()) {
        throw createAbortError()
      }

      workerUrl = await this.workerUrl
      worker = await this.workerPool.getWorker(workerUrl)
      if (isCanceled()) {
        throw createAbortError()
      }

      const cleanupTask = cleanup
      const shouldDropResult = isCanceled
      const releaseTaskCounter = releaseCounter
      const resetTaskNode = resetNode

      worker.onmessage = (e) => {
        if (finished) {
          return
        }
        finished = true
        let geometry: THREE.BufferGeometry | undefined
        let settledStatus: NodeLoadSettledStatus | null = null
        let settledReason: string | undefined
        try {
          const data = e.data
          const buffers = data.attributeBuffers

          geometry = new THREE.BufferGeometry()
          let outputBytes = 0
          let preciseBytes = 0
          let attributeCount = 0
          const returnedSourceBuffer = data.buffer?.buffer ?? data.buffer
          const returnedSourceBytes = returnedSourceBuffer?.byteLength ?? 0

          for (const property in buffers) {
            const propertyBuffer = buffers[property].buffer
            outputBytes += propertyBuffer?.byteLength ?? 0
            preciseBytes += buffers[property].preciseBuffer?.byteLength ?? 0
            attributeCount += 1

            if (property === 'position') {
              geometry.setAttribute(
                'position',
                new THREE.BufferAttribute(new Float32Array(propertyBuffer), 3)
              )
            } else if (property === 'rgba') {
              geometry.setAttribute(
                'rgba',
                new THREE.BufferAttribute(new Uint8Array(propertyBuffer), 4, true)
              )
            } else if (property === 'NORMAL') {
              geometry.setAttribute(
                'normal',
                new THREE.BufferAttribute(new Float32Array(propertyBuffer), 3)
              )
            } else if (property === 'INDICES') {
              const bufferAttribute = new THREE.BufferAttribute(new Uint8Array(propertyBuffer), 4)
              bufferAttribute.normalized = true
              geometry.setAttribute('indices', bufferAttribute)
            } else {
              const bufferAttribute: any = new THREE.BufferAttribute(
                new Float32Array(propertyBuffer),
                1
              )

              const batchAttribute = buffers[property].attribute
              bufferAttribute.potree = {
                offset: buffers[property].offset,
                scale: buffers[property].scale,
                preciseBuffer: buffers[property].preciseBuffer,
                range: batchAttribute.range,
              }

              geometry.setAttribute(property, bufferAttribute)
            }
          }
          if (shouldLogNodeDiagnostic(Math.max(inputBytes, outputBytes))) {
            diagnosticsLog.info(
              `[OctreeLoader] node decoded, name=${node.name}, level=${node.level}, numPoints=${node.numPoints}, inputMB=${toMB(inputBytes)}, outputMB=${toMB(outputBytes)}, preciseMB=${toMB(preciseBytes)}, returnedSourceMB=${toMB(returnedSourceBytes)}, attributes=${attributeCount}`
            )
          }

          if (shouldDropResult()) {
            geometry.dispose()
            resetTaskNode()
            settledStatus = 'late-dropped'
          } else {
            if (node.completeDecoded(requestToken, geometry, data.density)) {
              settledStatus = 'loaded'
            } else {
              geometry.dispose()
              settledStatus = 'late-dropped'
            }
          }
        } catch (err) {
          geometry?.dispose()
          settledStatus = 'failed'
          settledReason = getErrorMessage(err)
          markNodeLoadFailed(settledReason)
          log.warn(`点云节点解码结果处理失败，节点: ${node.name}, 原因: ${settledReason}`)
        } finally {
          returnWorker()
          releaseTaskCounter()
          cleanupTask()
          if (settledStatus) {
            settle(settledStatus, settledReason)
          }
        }
      }

      /** worker 异常时回收资源，避免加载状态永久阻塞 */
      const handleWorkerFailure = (event?: Event) => {
        if (finished) {
          return
        }
        finished = true
        discardWorker()
        const reason = getWorkerFailureMessage(event)
        markNodeLoadFailed(reason)
        releaseTaskCounter()
        cleanupTask()
        settle('failed', reason)
      }
      worker.onerror = handleWorkerFailure
      worker.onmessageerror = handleWorkerFailure

      const pointAttributes = node.octreeGeometry.pointAttributes
      const scale = node.octreeGeometry.scale

      const box = node.boundingBox
      const min = node.octreeGeometry.offset?.clone().add(box.min)
      const size = box.max.clone().sub(box.min)
      const max = min?.clone().add(size)
      const numPoints = node.numPoints

      const offset = node.octreeGeometry.loader?.offset

      const message = {
        encoding: this.metadata.encoding,
        name: node.name,
        buffer: buffer,
        pointAttributes: pointAttributes,
        scale: scale,
        min: min,
        max: max,
        size: size,
        offset: offset,
        numPoints: numPoints,
      }

      worker.postMessage(message, [message.buffer])
    } catch (err) {
      if (isAbortError(err)) {
        finishWithoutGeometry()
        settle('aborted')
      } else {
        const reason = getErrorMessage(err)
        markNodeLoadFailed(reason)
        finishWithoutGeometry()
        settle('failed', reason)
        log.warn(`点云节点加载失败，节点: ${node.name}, 原因: ${reason}`)
      }
    }
  }

  private notifyNodeLoadSettled(
    node: OctreeGeometryNode,
    status: NodeLoadSettledStatus,
    reason?: string
  ): void {
    this.onNodeLoadSettled?.({ node, status, reason })
  }

  /** 取消当前 loader 发起的未完成节点加载，阻止迟到 worker 结果写回旧节点。 */
  dispose(): void {
    if (this.disposed) {
      return
    }
    this.disposed = true
    this.generation++
    for (const controller of this.activeControllers) {
      controller.abort()
    }
    for (const cancel of Array.from(this.activeCancelers)) {
      cancel()
    }
  }

  /**
   * 解析层次结构二进制，构建八叉树节点拓扑。
   *
   * @param node - 待展开的起始节点（通常是代理节点）
   * @param buffer - hierarchy.bin 的原始数据
   */
  parseHierarchy(node: OctreeGeometryNode, buffer: ArrayBuffer) {
    const view = new DataView(buffer)

    const bytesPerNode = 22
    const numNodes = buffer.byteLength / bytesPerNode

    const octree = node.octreeGeometry
    const nodes: Array<OctreeGeometryNode | null> = new Array(numNodes)
    nodes[0] = node
    let nodePos = 1

    for (let i = 0; i < numNodes; i++) {
      const current = nodes[i]!

      const type = view.getUint8(i * bytesPerNode + 0)
      const childMask = view.getUint8(i * bytesPerNode + 1)
      const numPoints = view.getUint32(i * bytesPerNode + 2, true)
      const byteOffset = view.getBigInt64(i * bytesPerNode + 6, true)
      const byteSize = view.getBigInt64(i * bytesPerNode + 14, true)

      if (current?.nodeType === 2) {
        current.byteOffset = byteOffset
        current.byteSize = byteSize
        current.numPoints = numPoints
      } else if (type === 2) {
        current.hierarchyByteOffset = byteOffset
        current.hierarchyByteSize = byteSize
        current.numPoints = numPoints
      } else if (current) {
        current.byteOffset = byteOffset
        current.byteSize = byteSize
        current.numPoints = numPoints
      }

      if (current?.byteSize === 0n) {
        this.normalizeZeroByteNode(current)
        current.markStructural()
      }

      if (current) {
        current.nodeType = type
      }

      if (current?.nodeType === 2) {
        continue
      }

      for (let childIndex = 0; childIndex < 8; childIndex++) {
        const childExists = ((1 << childIndex) & childMask) !== 0

        if (!childExists) {
          continue
        }

        if (!current) {
          continue
        }

        const childName = current.name + childIndex

        const childAABB = createChildAABB(current.boundingBox, childIndex)
        const child = new OctreeGeometryNode(childName, octree, childAABB, this.potreeConfig)
        child.name = childName
        child.spacing = current.spacing! / 2
        child.level = current.level! + 1

        current.children[childIndex] = child
        child.parent = current

        nodes[nodePos] = child
        nodePos++
      }
    }
  }

  /**
   * 从远端拉取指定节点的层次结构数据并解析。
   *
   * @param node - 需展开的代理节点
   * @param signal - 可选 AbortSignal，中止时取消 fetch
   */
  async loadHierarchy(node: OctreeGeometryNode, signal?: AbortSignal) {
    const { hierarchyByteOffset, hierarchyByteSize } = node
    const hierarchyPath = resolveSiblingResourceUrl(this.url, 'hierarchy.bin')

    const first = hierarchyByteOffset!
    const last = first + hierarchyByteSize! - 1n

    const response = await fetch(hierarchyPath, {
      headers: {
        Range: `bytes=${first}-${last}`,
      },
      signal,
      ...(this.options.withCredentials === true
        ? { credentials: 'include' as RequestCredentials }
        : {}),
    })

    assertPartialContentResponse(response, 'hierarchy.bin')
    const buffer = await response.arrayBuffer()
    assertExpectedByteLength(buffer, bigIntToFiniteNumber(hierarchyByteSize), 'hierarchy.bin')

    this.parseHierarchy(node, buffer)
  }

  private normalizeZeroByteNode(node: OctreeGeometryNode): void {
    if (node.numPoints > 0) {
      log.warn(`点云零字节节点元数据矛盾，节点: ${node.name}, 声明点数: ${node.numPoints}`)
    }
    node.numPoints = 0
  }
}

const tmpVec3 = new THREE.Vector3()

function createChildAABB(aabb: THREE.Box3, index: number) {
  const min = aabb.min.clone()
  const max = aabb.max.clone()
  const size = tmpVec3.subVectors(max, min)

  if ((index & 0b0001) > 0) {
    min.z += size.z / 2
  } else {
    max.z -= size.z / 2
  }

  if ((index & 0b0010) > 0) {
    min.y += size.y / 2
  } else {
    max.y -= size.y / 2
  }

  if ((index & 0b0100) > 0) {
    min.x += size.x / 2
  } else {
    max.x -= size.x / 2
  }

  return new THREE.Box3(min, max)
}

const typenameTypeattributeMap = {
  double: PointAttributeTypes.DATA_TYPE_DOUBLE,
  float: PointAttributeTypes.DATA_TYPE_FLOAT,
  int8: PointAttributeTypes.DATA_TYPE_INT8,
  uint8: PointAttributeTypes.DATA_TYPE_UINT8,
  int16: PointAttributeTypes.DATA_TYPE_INT16,
  uint16: PointAttributeTypes.DATA_TYPE_UINT16,
  int32: PointAttributeTypes.DATA_TYPE_INT32,
  uint32: PointAttributeTypes.DATA_TYPE_UINT32,
  int64: PointAttributeTypes.DATA_TYPE_INT64,
  uint64: PointAttributeTypes.DATA_TYPE_UINT64,
}

/** 八叉树加载器，负责加载 metadata 并构建八叉树几何结构 */
export class OctreeLoader {
  /**
   * 将 metadata.json 中的属性数组转换为运行时 PointAttributes。
   *
   * @param jsonAttributes - 原始属性数组，字段见 Potree V2 规范
   * @returns 聚合后的 PointAttributes 实例
   */
  static parseAttributes(jsonAttributes: any[]) {
    const attributes = new PointAttributes()

    const replacements: Record<string, string> = {
      rgb: 'rgba',
    }

    for (const jsonAttribute of jsonAttributes) {
      const { name, numElements, min, max } = jsonAttribute

      const type = typenameTypeattributeMap[jsonAttribute.type]

      const potreeAttributeName = replacements[name] ? replacements[name] : name

      const attribute: any = new PointAttribute(potreeAttributeName, type, numElements)

      if (numElements === 1) {
        attribute.range = [min[0], max[0]]
      } else {
        attribute.range = [min, max]
      }

      if (name === 'gps-time') {
        // HACK: Guard against bad gpsTime range in metadata, see potree/potree#909
        if (attribute.range[0] === attribute.range[1]) {
          attribute.range[1] += 1
        }
      }

      attribute.initialRange = attribute.range

      attributes.add(attribute)
    }

    {
      const hasNormals =
        attributes.attributes.find((a) => a.name === 'NormalX') !== undefined &&
        attributes.attributes.find((a) => a.name === 'NormalY') !== undefined &&
        attributes.attributes.find((a) => a.name === 'NormalZ') !== undefined

      if (hasNormals) {
        const vector = {
          name: 'NORMAL',
          attributes: ['NormalX', 'NormalY', 'NormalZ'],
        }
        attributes.addVector(vector)
      }
    }

    return attributes
  }

  /**
   * 加载八叉树点云数据，通过 deps 注入外部依赖。
   *
   * @param url - metadata.json 的 URL
   * @param deps - 运行时依赖，含 workerPool、counter、potreeConfig 等
   * @returns `{ geometry }`，包含根节点与属性信息的 OctreeGeometry
   */
  static async load(url: string, deps: NodeLoaderDeps, options: NodeLoaderOptions = {}) {
    const response = await fetch(url, createFetchInit(options))
    const metadata = await response.json()

    const attributes = OctreeLoader.parseAttributes(metadata.attributes)

    const loader = new NodeLoader(deps, url, options)
    loader.metadata = metadata
    loader.attributes = attributes
    loader.scale = metadata.scale
    loader.offset = metadata.offset

    const octree = new OctreeGeometry()
    octree.url = url
    octree.spacing = metadata.spacing
    octree.scale = metadata.scale

    // 定位用
    const _position = metadata.attributes.find((x) => x.name === 'position')
    const _min = new THREE.Vector3(..._position.min)
    const _max = new THREE.Vector3(..._position.max)

    // 实际包围盒
    const min = new THREE.Vector3(...metadata.boundingBox.min)
    const max = new THREE.Vector3(...metadata.boundingBox.max)

    const boundingBox = new THREE.Box3(min, max)
    const mini_boundingBox = new THREE.Box3(_min, _max)

    const offset = min.clone()
    const mini_offset = _min.clone()
    boundingBox.min.sub(offset)
    boundingBox.max.sub(offset)
    mini_boundingBox.min.sub(mini_offset)
    mini_boundingBox.max.sub(mini_offset)

    octree.projection = metadata.projection
    octree.boundingBox = boundingBox
    octree.tightBoundingBox = boundingBox.clone()
    octree.mini_tightBoundingBox = mini_boundingBox.clone()
    octree.boundingSphere = boundingBox.getBoundingSphere(new THREE.Sphere())
    octree.tightBoundingSphere = boundingBox.getBoundingSphere(new THREE.Sphere())
    octree.offset = offset
    octree.pointAttributes = OctreeLoader.parseAttributes(metadata.attributes)
    octree.loader = loader

    const root = new OctreeGeometryNode('r', octree, boundingBox, deps.potreeConfig)
    root.level = 0
    root.nodeType = 2
    root.hierarchyByteOffset = 0n
    root.hierarchyByteSize = BigInt(metadata.hierarchy.firstChunkSize)
    root.hasChildren = false
    root.spacing = octree.spacing
    root.byteOffset = 0n

    octree.root = root

    loader.load(root)

    const result = {
      geometry: octree,
    }

    return result
  }
}
